import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { PropTypes } from 'prop-types';
import moment from 'moment';

// Animations
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const MetricsContainer = styled.div`
   background: #ffffff;
   border-radius: 8px;
   padding: 20px;
   margin: 16px 0;
   box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
   border: 1px solid #e5e7eb;
   animation: ${slideIn} 0.3s ease-out;
   width: 100%;
`;

const Header = styled.div`
   margin-bottom: 16px;
`;

const Title = styled.h1`
   color: #374151;
   margin: 0 0 4px 0;
   font-size: 20px;
   font-weight: 700;
`;

const Subtitle = styled.p`
   color: #6b7280;
   margin: 0;
   font-size: 14px;
`;

const StreamInfo = styled.div`
   background: #f9fafb;
   border-radius: 6px;
   padding: 12px;
   margin-bottom: 16px;
   display: flex;
   justify-content: space-between;
   align-items: center;
   flex-wrap: wrap;
   gap: 12px;
`;

const StreamName = styled.div`
   font-size: 14px;
   font-weight: 600;
   color: #374151;
`;

const StreamLabel = styled.span`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-bottom: 2px;
   margin-right: 3px;
`;

const StreamTime = styled.div`
   display: flex;
   gap: 16px;
   font-size: 12px;
   color: #6b7280;
`;

const TimeItem = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;

   .label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
   }

   .value {
      font-weight: 600;
      color: #374151;
   }
`;

const MetricsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, 1fr);
   gap: 12px;
`;

const MetricCard = styled.div`
   background: #f9fafb;
   border-radius: 6px;
   padding: 12px;
   border: 1px solid #e5e7eb;
   transition: all 0.2s ease;

   &:hover {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
   }
`;

const MetricHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin-bottom: 6px;
`;

const MetricTitle = styled.h3`
   color: #374151;
   font-size: 12px;
   font-weight: 500;
   margin: 0;
`;

const StatusIndicator = styled.div`
   width: 6px;
   height: 6px;
   border-radius: 50%;
   background-color: ${(props) => props.color};
`;

const MetricValue = styled.div`
   font-size: 20px;
   font-weight: 700;
   color: ${(props) => props.color || '#374151'};
   margin: 4px 0;
   ${(props) =>
      props.warning &&
      css`
         animation: ${pulse} 2s infinite;
      `}
`;

const MetricUnit = styled.span`
   font-size: 12px;
   color: #6b7280;
   margin-left: 4px;
   font-weight: 400;
`;

const BitrateBar = styled.div`
   width: 100%;
   height: 4px;
   background: #e5e7eb;
   border-radius: 2px;
   overflow: hidden;
   margin-top: 6px;
`;

const BitrateProgress = styled.div`
   height: 100%;
   background: ${(props) => props.color || '#10b981'};
   border-radius: 2px;
   width: ${(props) => Math.min((props.value / 5000) * 100, 100)}%;
   transition: width 0.3s ease;
`;

// Utility functions
const getStatusColor = (type, value) => {
   switch (type) {
      case 'maxFrameDelay':
         if (value >= 1200) return '#ef4444';
         if (value >= 500) return '#f59e0b';
         return '#10b981';

      case 'streamTimeRatio':
         if (Math.abs(value - 1) >= 0.14) return '#ef4444';
         if (Math.abs(value - 1) >= 0.07) return '#f59e0b';
         return '#10b981';

      default:
         return '#10b981';
   }
};

const formatValue = (key, value) => {
   switch (key) {
      case 'bitrate':
         return Math.round(value);
      case 'maxFrameDelay':
         return Math.round(value);
      case 'streamTimeRatio':
         return value.toFixed(3);
      case 'elapsedTime':
         return formatDuration(value);
      default:
         return typeof value === 'number' ? value.toFixed(2) : value;
   }
};

const formatDuration = (seconds) => {
   const duration = moment.duration(seconds, 'seconds');
   const hours = Math.floor(duration.asHours());
   const minutes = duration.minutes();
   const secs = duration.seconds();

   if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
   }
   return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const getUnit = (key) => {
   switch (key) {
      case 'bitrate':
         return 'kbps';
      case 'maxFrameDelay':
         return 'ms';
      case 'streamTimeRatio':
         return '';
      case 'elapsedTime':
         return '';
      default:
         return '';
   }
};

const getDisplayName = (key) => {
   const names = {
      bitrate: 'Bitrate',
      maxFrameDelay: 'Max Frame Delay',
      streamTimeRatio: 'Stream Time Ratio',
      elapsedTime: 'Stream Duration (HH:MM:SS)',
   };
   return names[key] || key;
};

// Main Component
const StreamMetricsDisplay = ({ inputMetrics, stream }) => {
   const [currentTime, setCurrentTime] = useState(moment());
   const [metrics, setMetrics] = useState({
      bitrate: 0,
      maxFrameDelay: 0,
      streamTimeRatio: 0,
      elapsedTime: 0,
      timestamp: moment.utc(),
   });

   useEffect(() => {
      const timeInterval = setInterval(() => {
         setCurrentTime(moment.utc());
      }, 1000);

      return () => clearInterval(timeInterval);
   }, []);

   useEffect(() => {
      if (inputMetrics) {
         setMetrics(inputMetrics);
      }
   }, [inputMetrics]);

   const lastUpdate = metrics.timestamp;

   return (
      <MetricsContainer>
         <Header>
            <Title>Stream Ingest Metrics</Title>
            <Subtitle>Live performance monitoring</Subtitle>
         </Header>

         <StreamInfo>
            <StreamName>
               <StreamLabel>Stream:</StreamLabel> {stream}
            </StreamName>
            <StreamTime>
               <TimeItem>
                  <div className='label'>Now [UTC]</div>
                  <div className='value'>{currentTime.format('HH:mm:ss')}</div>
               </TimeItem>
               <TimeItem>
                  <div className='label'>Stats Updated</div>
                  <div className='value'>{lastUpdate.format('HH:mm:ss')}</div>
               </TimeItem>
            </StreamTime>
         </StreamInfo>

         <MetricsGrid>
            {Object.entries(metrics)
               .filter(([key]) => key !== 'timestamp')
               .map(([key, value]) => {
                  const color = getStatusColor(key, value);
                  const isWarning = color === '#f59e0b';
                  const isCritical = color === '#ef4444';

                  return (
                     <MetricCard key={key}>
                        <MetricHeader>
                           <MetricTitle>{getDisplayName(key)}</MetricTitle>
                           <StatusIndicator color={color} />
                        </MetricHeader>
                        <MetricValue color={color} warning={isWarning || isCritical}>
                           {formatValue(key, value)}
                           <MetricUnit>{getUnit(key)}</MetricUnit>
                        </MetricValue>
                        {key === 'bitrate' && (
                           <BitrateBar>
                              <BitrateProgress value={value} color={color} />
                           </BitrateBar>
                        )}
                     </MetricCard>
                  );
               })}
         </MetricsGrid>
      </MetricsContainer>
   );
};

StreamMetricsDisplay.propTypes = {
   inputMetrics: PropTypes.exact({
      bitrate: PropTypes.number,
      maxFrameDelay: PropTypes.number,
      streamTimeRatio: PropTypes.number,
      elapsedTime: PropTypes.number,
      timestamp: moment.isMoment,
   }),
   stream: PropTypes.string,
};

export default StreamMetricsDisplay;
